/**
 * IWOS Raw Message Parser
 * Parses raw IWOS message format into frontend data structure
 *
 * Reference: IWOS Message Format Update (DEC2025).xlsx
 */

// Helper to create field object with value
const createField = (value, ts = null) => {
  const field = { value: String(value) };
  if (ts) field.ts = ts;
  return field;
};

// Celsius to Fahrenheit
const celsiusToFahrenheit = (c) => (parseFloat(c) * 9/5) + 32;

// Knots to mph
const knotsToMph = (kt) => parseFloat(kt) * 1.15078;

// Knots to m/s
const knotsToMps = (kt) => parseFloat(kt) * 0.514444;

// Meters to feet
const metersToFeet = (m) => parseFloat(m) * 3.28084;

// Inches to mm
const inchesToMm = (inches) => parseFloat(inches) * 25.4;

// km to statute miles
const kmToStatuteMiles = (km) => parseFloat(km) * 0.621371;

// km to meters
const kmToMeters = (km) => parseFloat(km) * 1000;

/**
 * Parse IWOS timestamp
 * Format: -YY/MM/DD,HH:MM:SS or YY/MM/DD,HH:MM:SS
 * Leading '-' indicates no GPS sync
 */
const parseIWOSTimestamp = (dateStr) => {
  if (!dateStr || dateStr === '?') return null;

  const cleanStr = dateStr.replace(/^-/, '');
  const [datePart, timePart] = cleanStr.split(',');
  if (!datePart || !timePart) return null;

  const [yy, mm, dd] = datePart.split('/');
  const [hh, min, ss] = timePart.split(':');

  const year = 2000 + parseInt(yy, 10);
  return new Date(Date.UTC(
    year,
    parseInt(mm, 10) - 1,
    parseInt(dd, 10),
    parseInt(hh, 10),
    parseInt(min, 10),
    parseInt(ss, 10)
  )).getTime();
};

/**
 * Calculate dew point from temperature (C) and relative humidity (%)
 * Using Magnus formula approximation
 */
const calculateDewPoint = (tempC, humidity) => {
  const t = parseFloat(tempC);
  const rh = parseFloat(humidity);
  if (isNaN(t) || isNaN(rh) || rh <= 0) return null;

  const a = 17.27;
  const b = 237.7;
  const alpha = ((a * t) / (b + t)) + Math.log(rh / 100);
  const dewPointC = (b * alpha) / (a - alpha);
  return dewPointC;
};

/**
 * Parse a raw IWOS message into frontend data structure
 *
 * @param {string} rawMessage - Raw IWOS message text
 * @returns {object} Parsed data in frontend format
 */
export const parseIWOSRawMessage = (rawMessage) => {
  const lines = rawMessage.trim().split('\n').map(l => l.trim()).filter(l => l && l !== '~');
  const parsed = {};
  let timestamp = null;
  let tempC = null;
  let humidity = null;

  for (const line of lines) {
    // Handle message type header
    if (line.startsWith('@')) {
      const typeNum = line.substring(1);
      parsed.messageType = createField(typeNum);
      continue;
    }

    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const tag = line.substring(0, colonIndex);
    const data = line.substring(colonIndex + 1);
    const parts = data.split(':');

    switch (tag) {
      case 'T': // Time: T:-25/10/07,11:16:30
        timestamp = parseIWOSTimestamp(data);
        if (timestamp) {
          parsed.timestamp = timestamp;
        }
        break;

      case 'N': // Message number: N:176:0:0:0:KQXX:1
        parsed.messageNumber = createField(parts[0], timestamp);
        if (parts[3] === 'S') {
          parsed.speciFlag = createField('S', timestamp);
        }
        if (parts[4] && parts[4] !== '?' && parts[4] !== '0') {
          parsed.ICAO = createField(parts[4], timestamp);
        }
        break;

      case 'B': // Battery: B:48.1:60:0:e1:1:5:00
        parsed.battery = createField(parts[0], timestamp);
        parsed.mode = createField(parts[1], timestamp);
        break;

      case 'LA': // Latitude: LA:35.17981
        parsed.lat = createField(parts[0], timestamp);
        break;

      case 'LO': // Longitude: LO:-73.31028
        parsed.lon = createField(parts[0], timestamp);
        break;

      case 'EL': // Elevation: EL:39.0
        const elevM = parseFloat(parts[0]);
        parsed.elevation = createField(parts[0], timestamp);
        if (!isNaN(elevM)) {
          parsed.elevationFt = createField(metersToFeet(elevM).toFixed(1), timestamp);
        }
        break;

      case 'OR': // Orientation: OR:75:-1:18
        parsed.compass = createField(parts[0], timestamp);
        parsed.tip = createField(parts[1], timestamp);
        parsed.orientation = createField(parts[2], timestamp);
        break;

      case 'TA': // Temperature: TA:24.6:24.8:00
        tempC = parts[0];
        parsed.temperatureC = createField(parts[0], timestamp);
        const tempF = celsiusToFahrenheit(parts[0]);
        if (!isNaN(tempF)) {
          parsed.temperatureF = createField(tempF.toFixed(1), timestamp);
        }
        // 10 min average
        if (parts[1] && parts[1] !== '?') {
          parsed.temperature10minC = createField(parts[1], timestamp);
        }
        break;

      case 'BA': // Barometric: BA:1010.1:2992.2:3.5:3:8
        // 1) Pressure (mb)
        const pressureMb = parseFloat(parts[0]);
        parsed.stationPressureMb = createField(parts[0], timestamp);
        if (!isNaN(pressureMb)) {
          // Convert mb to inHg
          parsed.stationPressure = createField((pressureMb * 0.02953).toFixed(2), timestamp);
        }
        // 2) Altimeter (inHg x 100 according to spec, but sample shows 2992.2)
        if (parts[1] && parts[1] !== '?') {
          const altVal = parseFloat(parts[1]);
          // If value > 100, it's likely inHg x 100 format
          if (altVal > 100) {
            parsed.altimeter = createField((altVal / 100).toFixed(2), timestamp);
            parsed.altimeterMb = createField(((altVal / 100) / 0.02953).toFixed(1), timestamp);
          } else {
            parsed.altimeter = createField(parts[1], timestamp);
            parsed.altimeterMb = createField((parseFloat(parts[1]) / 0.02953).toFixed(1), timestamp);
          }
        }
        // 3) 3 hour differential (pressure tendency)
        if (parts[2] && parts[2] !== '?') {
          parsed.pressureTendency3hr = createField(parts[2], timestamp);
        }
        // 4) Rapid change flag (3=rapid rise, 4=rapid fall)
        // 5) Pressure tendency code
        if (parts[4] && parts[4] !== '?') {
          parsed.threeHourTendency = createField(parts[4], timestamp);
        }
        break;

      case 'RH': // Relative humidity: RH:51.2:49.9
        humidity = parts[0];
        parsed.humidity = createField(parts[0], timestamp);
        if (parts[1] && parts[1] !== '?') {
          parsed.humidity10min = createField(parts[1], timestamp);
        }
        break;

      case 'WI': // Wind: WI:10.5:210:12.0:200:12.0:200:FF:00
        // 1) Average wind speed (kt)
        // 2) Direction (degrees)
        parsed.windSpeed = createField(parts[0], timestamp);
        parsed.windDir = createField(parts[1], timestamp);

        // 3) 2 min avg speed, 4) 2 min avg dir
        const wind2minKt = parseFloat(parts[2]);
        parsed.windSpeed2minKt = createField(parts[2], timestamp);
        parsed.windDir2min = createField(parts[3], timestamp);
        if (!isNaN(wind2minKt)) {
          parsed.windSpeed2minMph = createField(knotsToMph(wind2minKt).toFixed(1), timestamp);
          parsed.windSpeed2minMps = createField(knotsToMps(wind2minKt).toFixed(1), timestamp);
        }

        // 5) 10 min avg speed, 6) 10 min avg dir
        const wind10minKt = parseFloat(parts[4]);
        parsed.windSpeed10minKt = createField(parts[4], timestamp);
        parsed.windDir10min = createField(parts[5], timestamp);
        if (!isNaN(wind10minKt)) {
          parsed.windSpeed10minMph = createField(knotsToMph(wind10minKt).toFixed(1), timestamp);
          parsed.windSpeed10minMps = createField(knotsToMps(wind10minKt).toFixed(1), timestamp);
        }
        break;

      case 'GU': // Gust: GU:24.0:200:24.0:200:12.0:0:?:?
        // 1) 10 min max speed, 2) 10 min max dir
        const maxKt = parseFloat(parts[0]);
        parsed.maxWindSpeed = createField(parts[0], timestamp);
        parsed.maxWindDir = createField(parts[1], timestamp);
        if (!isNaN(maxKt)) {
          parsed.maxWindSpeedMph = createField(knotsToMph(maxKt).toFixed(1), timestamp);
          parsed.maxWindSpeedMps = createField(knotsToMps(maxKt).toFixed(1), timestamp);
        }
        // 3) Gust speed (kt), 4) Gust dir
        if (parts[2] && parts[2] !== '?') {
          parsed.gustSpeedKt = createField(parts[2], timestamp);
        }
        if (parts[3] && parts[3] !== '?') {
          parsed.gustDir = createField(parts[3], timestamp);
        }
        // 5) Spread magnitude
        // 6) Squall flag
        if (parts[5] && parts[5] !== '?') {
          parsed.squallFlag = createField(parts[5], timestamp);
        }
        // 7) Variability start, 8) Variability end
        if (parts[6] && parts[6] !== '?') {
          parsed.windVarStart = createField(parts[6], timestamp);
        }
        if (parts[7] && parts[7] !== '?') {
          parsed.windVarEnd = createField(parts[7], timestamp);
        }
        break;

      case 'CL': // Cloud: CL:1aa10:012:030:100:635:000:000:2:5:7:8:0:0:300:0:00
        // 1) Reserved
        // 2-7) Cloud heights layers 1-6 (tens of meters)
        // 8-13) Coverage layers 1-6 (oktas)
        // 14) Lowest broken layer (meters) or '?'
        // 15) Vertical visibility
        // 16) BIT code

        const heights = [];
        const coverages = [];

        // Heights already in meters from backend - no conversion needed
        for (let i = 1; i <= 6; i++) {
          if (parts[i] && parts[i] !== '000' && parts[i] !== '0' && parts[i] !== '?') {
            const heightM = parseInt(parts[i], 10);
            heights.push(heightM);
          }
        }

        // Coverages (oktas)
        for (let i = 7; i <= 12; i++) {
          if (parts[i] !== undefined) {
            coverages.push(parts[i]);
          }
        }

        parsed.cloudLayers = createField(heights.length > 0 ? heights.join(',') : 'CLR', timestamp);
        parsed.cloudCoverage = createField(coverages.join(','), timestamp);

        // Lowest broken layer (ceiling) - already in meters, no conversion needed
        if (parts[13] && parts[13] !== '?' && parts[13] !== '0') {
          const ceilingM = parseInt(parts[13], 10);
          parsed.lowestCloud = createField(ceilingM.toString(), timestamp);
        }

        // Vertical visibility
        if (parts[14] && parts[14] !== '0' && parts[14] !== '?') {
          parsed.verticalVisibility = createField(parts[14], timestamp);
        }
        break;

      case 'IR': // Iridium: IR:0:0:0:300534060613880:TA19002:EMSS:00
        // 4) IMEI
        if (parts[3] && parts[3] !== '?') {
          parsed.imei = createField(parts[3], timestamp);
        }
        break;

      case 'LT': // Lightning: LT:02:008:0020:002:008:025:00
        // 1) Strikes last minute
        // 2) Strikes last 10 min
        // 3) Strikes last 60 min
        // 4) Min distance 1 min (NM)
        // 5) Min distance 10 min (NM)
        // 6) Min distance 60 min (NM)
        parsed.lightningFreq = createField(parts[0], timestamp);
        parsed.lightningFreq10 = createField(parts[1], timestamp);
        parsed.lightningFreq60 = createField(parts[2], timestamp);

        if (parts[3] && parts[3] !== '?') {
          parsed.lightningDist = createField(parts[3], timestamp);
        }
        if (parts[4] && parts[4] !== '?') {
          parsed.lightningDist10 = createField(parts[4], timestamp);
        }
        if (parts[5] && parts[5] !== '?') {
          parsed.lightningDist60 = createField(parts[5], timestamp);
        }
        break;

      case 'PA': // Precipitation: PA:0.01:0.01:0.20:1.20:0.00:0.00:0.01:0.20:1.20:0
        // 1) Rate 1 min (in/hr)
        // 2) Rate 1 hr avg
        // 3) Rate 3 hr avg
        // 4) Rate 6 hr avg
        // 5) Rate 24 hr avg
        // 6) Accum 1 hr
        // 7) Accum 3 hr
        // 8) Accum 6 hr
        // 9) Accum 24 hr
        parsed.precip = createField(parts[0], timestamp);
        parsed.precipMm = createField(inchesToMm(parts[0]).toFixed(2), timestamp);

        // Accumulations
        if (parts[5] !== undefined) {
          parsed.precipAcc1hr = createField(parts[5], timestamp);
          parsed.precipAcc1hrMm = createField(inchesToMm(parts[5]).toFixed(2), timestamp);
        }
        if (parts[6] !== undefined) {
          parsed.precipAcc3hr = createField(parts[6], timestamp);
          parsed.precipAcc3hrMm = createField(inchesToMm(parts[6]).toFixed(2), timestamp);
        }
        if (parts[7] !== undefined) {
          parsed.precipAcc6hr = createField(parts[7], timestamp);
          parsed.precipAcc6hrMm = createField(inchesToMm(parts[7]).toFixed(2), timestamp);
        }
        if (parts[8] !== undefined) {
          parsed.precipAcc24hr = createField(parts[8], timestamp);
          parsed.precipAcc24hrMm = createField(inchesToMm(parts[8]).toFixed(2), timestamp);
        }
        break;

      case 'PW': // Present Weather: PW:0:95:0:12.0:15.2:0:00
        // 1) Product ID
        // 2) Precipitation WMO code
        // 3) Precip rate (in/hr)
        // 4) Visibility 1 min (km)
        // 5) Visibility 10 min (km)
        // 6) Obscuration WMO code

        // WMO weather code
        if (parts[1] && parts[1] !== '0' && parts[1] !== '00') {
          parsed.weather = createField(parts[1], timestamp);
        }

        // Visibility in km, convert to statute miles for US display
        if (parts[3] && parts[3] !== '?') {
          const visKm = parseFloat(parts[3]);
          parsed.visibility1min = createField(parts[3], timestamp);
          if (!isNaN(visKm)) {
            parsed.visibility = createField(kmToStatuteMiles(visKm).toFixed(1), timestamp);
            parsed.visibilityMeters = createField(kmToMeters(visKm).toFixed(0), timestamp);
          }
        }
        if (parts[4] && parts[4] !== '?') {
          parsed.visibility10Min = createField(parts[4], timestamp);
        }
        break;

      case 'SS': // Sea State: SS:96.82:3.28:7.22:178:00
        // 1) Compass heading
        // 2) Sig wave height (m)
        // 3) Dominant period (s)
        // 4) Dominant direction (deg)
        if (parts[0] && parts[0] !== '?') {
          parsed.seaStateCompass = createField(parts[0], timestamp);
        }
        if (parts[1] && parts[1] !== '?') {
          const waveHtM = parseFloat(parts[1]);
          parsed.sigWaveHgt = createField(parts[1], timestamp);
          if (!isNaN(waveHtM)) {
            parsed.sigWaveHgtFt = createField(metersToFeet(waveHtM).toFixed(1), timestamp);
          }
        }
        if (parts[2] && parts[2] !== '?') {
          parsed.sigWavePrd = createField(parts[2], timestamp);
        }
        if (parts[3] && parts[3] !== '?') {
          parsed.sigWaveDir = createField(parts[3], timestamp);
        }
        break;

      default:
        // Unknown tag, skip
        break;
    }
  }

  // Calculate derived values
  if (tempC && humidity) {
    const dewPointC = calculateDewPoint(tempC, humidity);
    if (dewPointC !== null) {
      parsed.dewPoint = createField(dewPointC.toFixed(1), timestamp);
      parsed.dewPointF = createField(celsiusToFahrenheit(dewPointC).toFixed(1), timestamp);
    }
  }

  return parsed;
};

export default parseIWOSRawMessage;
